import React, { useState } from 'react';
import { Button, Typography, Input, message, Form } from 'antd';
import { ChevronDown, ChevronRight } from '@carbon/icons-react';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import legacyService from 'services/legacy.service';
import { additionalItemsExpandedAtom } from '../../../atoms';
import ChecklistUploadFile from './ChecklistUploadFile';
import PreviousSubmissions from './PreviousSubmissions';

const { Text } = Typography;
const { TextArea } = Input;

export default function AdditionalItemsSection({ applicationId }) {
  const [expanded, setExpanded] = useAtom(additionalItemsExpandedAtom);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      // Submit message with each uploaded file
      for (const file of values.files) {
        await legacyService.uploadAdditionalItem(applicationId, file.originFileObj, values.message);
      }

      message.success('Submitted successfully', 7);
      form.resetFields();
      setExpanded(false);
    } catch (error) {
      console.error('Error submitting:', error);
      message.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      id="additional-items-section"
      className={classNames('mt-10 mb-6 rounded-lg border border-dashed border-gray-300', {
        'bg-white': expanded,
      })}
    >
      {/* Collapsible Header */}
      <Button
        type="text"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center !justify-start gap-2 w-full px-4 py-8 text-left !bg-transparent transition-colors cursor-pointer"
      >
        {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        <Text className="font-medium text-base">Have something not listed above?</Text>
      </Button>

      {/* Content when expanded */}
      {expanded && (
        <div className="border-t border-gray-200">
          <div className="px-6 pb-6 pt-2">
            <Form form={form} onFinish={handleSubmit} layout="vertical">
              {/* Message textarea */}
              <Form.Item
                name="message"
                rules={[{ required: true, message: 'Please enter a message' }]}
                className="mb-4"
              >
                <TextArea
                  placeholder="Enter your question or additional information..."
                  rows={4}
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              {/* Attach documents section */}
              <div className="mt-8 mb-4">
                <Text className="font-semibold text-base block mb-3">Attach documents:</Text>
                <ChecklistUploadFile />
              </div>

              {/* Submit button */}
              <Form.Item className="mb-0">
                <Button
                  type="primary"
                  size="large"
                  block
                  htmlType="submit"
                  loading={submitting}
                  className="!text-base !font-semibold"
                >
                  {submitting ? 'Submitting...' : 'Submit to Child Care Aware'}
                </Button>
              </Form.Item>
            </Form>
          </div>

          {/* Previous Submissions Section */}
          <PreviousSubmissions applicationId={applicationId} isOpen={expanded} />
        </div>
      )}
    </div>
  );
}
